import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MuiButton from "@material-ui/core/Button";
import React, { FC, useEffect, useRef, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import AdGroupSelectionPanel from "../../../../views/redesign/AdGroupSelectionPanel/AdGroupSelectionPanel";
import BudgetAndTimelinePanel from "../../../../views/redesign/BudgetAndTimelinePanel/BudgetAndTimelinePanel";
import CRMRetargetingSegmentsView from "../../../../views/redesign/CRMRetargetingSegments/CRMRetargetingSegmentsView";
import CampaignTemplateDetailsPanel from "../../../../views/redesign/CampaignTemplateDetailsPanel/CampaignTemplateDetailsPanel";
import CampaignTemplateSummaryPanel from "../../../../views/redesign/CampaignTemplateSummaryPanel/CampaignTemplateSummaryPanel";
import CollapsiblePanel from "../CollapsiblePanel";
import LandingPageSelectionPanel from "../../../../views/redesign/LandingPageSelectionPanel/LandingPageSelectionPanel";
import NoPaymentMethodModal from "./NoPaymentMethodModal";
import PublicationListSetSelectorPanel from "../../../../views/redesign/PublicationListsView/PublicationListSetSelectorPanel";
import RoleTitleSetSelectorPanel from "../../../../views/redesign/RolesTitlesView/RoleTitleSetSelectorPanel";
import StateProvinceSetSelectorPanel from "../../../../views/redesign/StateProvinceView/StateProvinceSetSelectorPanel";
import SuccessDialog from "../../elements/SuccessDialog/SuccessDialog";
import TargetCompaniesView from "../../../../views/redesign/TargetCompaniesView/TargetCompaniesView";
import VisitorsRetargetingSegmentsView from "../../../../views/redesign/VisitorsRetargetingSegments/VisitorsRetargetingSegmentsView";
import ZipCodeSetSelectorPanel from "../../../../views/redesign/ZipCodesView/ZipCodeSetSelectorPanel";
import { CAMPAIGN_PLAY_TYPE } from "../../../../constants/campaignPlayTypes";
import { CAMPAIGN_STEPS } from "../../../../types/campaignTypes";
import { ROUTE_PATHS } from "../../../../types/RoutePaths";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import {
  createCampaign,
  createCampaignTemplate,
  resetCampaignToCreateState,
  setCampaignPlayType,
  setCampaignSteps,
  updateCampaignTemplate,
} from "../../../../redux/reducers/campaignToCreate";
import { errorToaster } from "../../../../helpers/toasters";
import { initializeGlobalAdvertiser } from "../../../../redux/reducers/agency";

export type CampaignBuilderMode = "template" | "campaign";

interface CampaignBuilderFormProps {
  mode: CampaignBuilderMode;
  campaignPlayType?: CAMPAIGN_PLAY_TYPE;
  visibleOptionalPanels?: CAMPAIGN_STEPS[];
  skipInitialReset?: boolean;
  onBack?: () => void;
  isEditMode?: boolean;
  onSuccess?: () => void;
}

const PageContent = styled("div")({
  paddingBottom: 96,
});

const PanelStack = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const StickyFooter = styled("div")(({ theme }: { theme: Theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 200,
  right: 0,
  background: theme.palette.brand.white,
  borderTop: `1px solid ${theme.palette.brand.gray200}`,
  padding: "12px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 12,
  zIndex: 100,
}));

const FooterErrorText = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.errorRed,
  marginRight: "auto",
}));

const CancelButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  color: "#374151",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  border: "1px solid #D1D5DB",
  textTransform: "none",
  boxShadow: "none",
  minWidth: "unset",
  "&:hover": {
    background: theme.palette.brand.gray50,
    boxShadow: "none",
  },
}));

const CreateButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 20px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  minWidth: 140,
  "&:hover": {
    background: theme.palette.brand.coralHover,
    transform: "translateY(-1px)",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    background: theme.palette.brand.coral,
    color: theme.palette.brand.white,
    opacity: 0.6,
  },
}));

const ConfirmDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: 10,
    padding: "8px 0",
    minWidth: 340,
  },
});

const ConfirmTitle = styled(DialogTitle)(({ theme }: { theme: Theme }) => ({
  "& .MuiTypography-root": {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.brand.gray900,
  },
}));

const ConfirmQuestion = styled(DialogContent)(({ theme }: { theme: Theme }) => ({
  fontSize: 14,
  color: theme.palette.brand.gray700,
  padding: "4px 24px 16px",
}));

const ConfirmActions = styled(DialogActions)({
  padding: "8px 24px 16px",
  gap: 8,
});

const ConfirmCancelButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  color: "#374151",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  border: "1px solid #D1D5DB",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    background: theme.palette.brand.gray50,
    boxShadow: "none",
  },
}));

const ConfirmYesButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 20px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    boxShadow: "none",
  },
}));

const BackButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  color: "#374151",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 8,
  border: "1px solid #D1D5DB",
  textTransform: "none",
  boxShadow: "none",
  minWidth: "unset",
  marginRight: "auto",
  "&:hover": {
    background: theme.palette.brand.gray50,
    boxShadow: "none",
  },
}));

const CampaignBuilderForm: FC<CampaignBuilderFormProps> = ({
  mode,
  campaignPlayType,
  visibleOptionalPanels,
  skipInitialReset,
  onBack,
  isEditMode,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isTemplateMode = mode === "template";

  const detailsSubmitRef = useRef<HTMLButtonElement>(null);
  const budgetSubmitRef = useRef<HTMLButtonElement>(null);

  const detailsPanelRef = useRef<HTMLDivElement>(null);
  const budgetPanelRef = useRef<HTMLDivElement>(null);
  const landingPagePanelRef = useRef<HTMLDivElement>(null);
  const adGroupPanelRef = useRef<HTMLDivElement>(null);
  const crmRetargetingPanelRef = useRef<HTMLDivElement>(null);
  const visitorsRetargetingPanelRef = useRef<HTMLDivElement>(null);
  const zipCodesPanelRef = useRef<HTMLDivElement>(null);
  const stateProvincePanelRef = useRef<HTMLDivElement>(null);
  const targetCompaniesPanelRef = useRef<HTMLDivElement>(null);
  const roleTitlesPanelRef = useRef<HTMLDivElement>(null);
  const publicationListsPanelRef = useRef<HTMLDivElement>(null);

  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [landingPageExpanded, setLandingPageExpanded] = useState(true);

  const [invalidPanels, setInvalidPanels] = useState<Set<string>>(new Set());
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(skipInitialReset ?? false);

  useEffect(() => {
    const initForm = async () => {
      if (!skipInitialReset) {
        await dispatch(resetCampaignToCreateState());
      }
      if (campaignPlayType) {
        dispatch(setCampaignPlayType(campaignPlayType));
      }
      dispatch(initializeGlobalAdvertiser());
      setIsInitialized(true);
    };

    initForm();
  }, [dispatch, skipInitialReset, campaignPlayType]);

  const showOptional = (step: CAMPAIGN_STEPS) => !visibleOptionalPanels || visibleOptionalPanels.includes(step);
  const hasPrescribedPanels = Boolean(visibleOptionalPanels);

  const campaignToCreate = useSelector((s: ReduxApplicationState) => s.campaignToCreate);
  const { campaignSteps } = campaignToCreate;

  const showStepToggle = isTemplateMode && isEditMode;
  const isStepIncluded = (step: CAMPAIGN_STEPS) => Boolean(campaignSteps?.includes(step));
  const toggleStepInclusion = (step: CAMPAIGN_STEPS, included: boolean) => {
    const updated = included ? [...(campaignSteps ?? []), step] : (campaignSteps ?? []).filter((s) => s !== step);
    dispatch(setCampaignSteps(updated));
  };
  const { isPending } = campaignToCreate;

  const handleCancel = () => {
    dispatch(resetCampaignToCreateState());
    history.push(isTemplateMode ? ROUTE_PATHS.CAMPAIGN_TEMPLATES : ROUTE_PATHS.ROOT);
  };

  const handleCreate = () => {
    setCreateError(null);
    setInvalidPanels(new Set());

    detailsSubmitRef.current?.click();
    budgetSubmitRef.current?.click();

    type ValidationBlock = {
      key: string;
      title: string;
      ref: React.RefObject<HTMLDivElement>;
      expand: () => void;
      isInvalid: () => boolean;
    };

    const blocks: ValidationBlock[] = [
      {
        key: "details",
        title: "Campaign Details",
        ref: detailsPanelRef,
        expand: () => setDetailsExpanded(true),
        isInvalid: () =>
          !campaignToCreate.selectedAdvertiser ||
          (isTemplateMode && !campaignToCreate.campaignTemplateName) ||
          !campaignToCreate.basicDetails.name ||
          !campaignToCreate.basicDetails.startDate ||
          !campaignToCreate.basicDetails.endDate,
      },
      {
        key: "budget",
        title: "Budget & Timeline",
        ref: budgetPanelRef,
        expand: () => {},
        isInvalid: () =>
          Number(campaignToCreate.basicDetails.dailyBudget) <= 0 ||
          Number(campaignToCreate.basicDetails.maxBid) <= 0 ||
          Number(campaignToCreate.basicDetails.maxBid) > Number(campaignToCreate.basicDetails.estimatedBudget),
      },
      {
        key: "landingPage",
        title: "Landing Page",
        ref: landingPagePanelRef,
        expand: () => setLandingPageExpanded(true),
        isInvalid: () => !campaignToCreate.landingPage,
      },
      {
        key: "adGroup",
        title: "Ad Group",
        ref: adGroupPanelRef,
        expand: () => {},
        isInvalid: () => campaignToCreate.adGroupToCreate.creativeAssets.length === 0,
      },
    ];

    if (!isTemplateMode && hasPrescribedPanels) {
      if (showOptional(CAMPAIGN_STEPS.CRM_RETARGETING_SEGMENTS)) {
        blocks.push({
          key: "crmRetargeting",
          title: "CRM Retargeting",
          ref: crmRetargetingPanelRef,
          expand: () => {},
          isInvalid: () => !campaignToCreate.foundEmailsInfo?.emailsToHash?.length,
        });
      }
      if (showOptional(CAMPAIGN_STEPS.VISITORS_RETARGETING_SEGMENTS)) {
        blocks.push({
          key: "visitorsRetargeting",
          title: "Visitors Re-targeting Segments",
          ref: visitorsRetargetingPanelRef,
          expand: () => {},
          isInvalid: () => !campaignToCreate.selectedSegmentIds?.length,
        });
      }
      if (showOptional(CAMPAIGN_STEPS.ZIP_CODES)) {
        blocks.push({
          key: "zipCodes",
          title: "Geography / Zip Codes",
          ref: zipCodesPanelRef,
          expand: () => {},
          isInvalid: () => !campaignToCreate.selectedZipCodeSetId && !campaignToCreate.selectedZipCodes?.length,
        });
      }
      if (showOptional(CAMPAIGN_STEPS.STATE_PROVINCES)) {
        blocks.push({
          key: "stateProvinces",
          title: "Geography (State/Province)",
          ref: stateProvincePanelRef,
          expand: () => {},
          isInvalid: () =>
            !campaignToCreate.selectedStateProvinceSetId && campaignToCreate.selectedGeoTargetOptions.length === 0,
        });
      }
      if (showOptional(CAMPAIGN_STEPS.TARGET_COMPANIES)) {
        blocks.push({
          key: "targetCompanies",
          title: "Target Companies",
          ref: targetCompaniesPanelRef,
          expand: () => {},
          isInvalid: () => !campaignToCreate.foundCompanyInfo?.uniqueSegments?.length,
        });
      }
      if (showOptional(CAMPAIGN_STEPS.ROLE_TITLES)) {
        blocks.push({
          key: "roleTitles",
          title: "Roles & Titles",
          ref: roleTitlesPanelRef,
          expand: () => {},
          isInvalid: () => !Object.values(campaignToCreate.campaignCookieSets ?? {}).find((v) => v.length),
        });
      }
      if (showOptional(CAMPAIGN_STEPS.PUBLICATION_LISTS)) {
        blocks.push({
          key: "publicationLists",
          title: "Publication Lists",
          ref: publicationListsPanelRef,
          expand: () => {},
          isInvalid: () =>
            (campaignToCreate.selectedDomainListsIds?.length ?? 0) === 0 &&
            (campaignToCreate.selectedDomainIds?.length ?? 0) === 0,
        });
      }
    }

    const invalid = new Set<string>();
    let firstInvalidBlock: ValidationBlock | null = null;

    for (const block of blocks) {
      if (block.isInvalid()) {
        invalid.add(block.key);
        if (!firstInvalidBlock) firstInvalidBlock = block;
      }
    }

    if (invalid.size > 0) {
      setInvalidPanels(invalid);
      if (firstInvalidBlock) {
        errorToaster(`Please fill in the ${firstInvalidBlock.title} section.`, 5000);
        firstInvalidBlock.expand();
        setTimeout(() => {
          firstInvalidBlock!.ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 350);
      }
      return;
    }

    setIsConfirmOpen(true);
  };

  const handleConfirmedCreate = () => {
    setIsConfirmOpen(false);
    if (isTemplateMode && isEditMode) {
      dispatch(updateCampaignTemplate(() => onSuccess?.()));
    } else if (isTemplateMode) {
      dispatch(
        createCampaignTemplate(
          () => setIsSuccessOpen(true),
          () => setCreateError("Failed to create template. Please try again."),
        ),
      );
    } else {
      dispatch(
        createCampaign(
          () => setIsSuccessOpen(true),
          () => setCreateError("Failed to create campaign. Please try again."),
        ),
      );
    }
  };

  const handleSuccessClose = () => {
    const advertiserId = campaignToCreate.selectedAdvertiser?.brandgenAdvertiserId;
    dispatch(resetCampaignToCreateState());
    setIsSuccessOpen(false);
    if (isTemplateMode) {
      history.push(ROUTE_PATHS.CAMPAIGN_TEMPLATES);
    } else {
      history.push(`/advertisers/${advertiserId}/dashboard`);
    }
  };

  if (!isInitialized) return null;

  return (
    <>
      <PageContent>
        <PanelStack>
          <CollapsiblePanel
            ref={detailsPanelRef}
            title="Campaign Details"
            required
            collapsible={false}
            isOpen={detailsExpanded}
            onToggle={() => setDetailsExpanded((v) => !v)}
            hasError={invalidPanels.has("details")}
          >
            <CampaignTemplateDetailsPanel mode={mode} submitRef={detailsSubmitRef} />
          </CollapsiblePanel>
          <CollapsiblePanel
            ref={budgetPanelRef}
            title="Budget & Timeline"
            required
            collapsible={false}
            hasError={invalidPanels.has("budget")}
          >
            <BudgetAndTimelinePanel submitRef={budgetSubmitRef} />
          </CollapsiblePanel>
          <CollapsiblePanel
            ref={landingPagePanelRef}
            title="Landing Page"
            required
            collapsible={false}
            isOpen={landingPageExpanded}
            onToggle={() => setLandingPageExpanded((v) => !v)}
            hasError={invalidPanels.has("landingPage")}
          >
            <LandingPageSelectionPanel />
          </CollapsiblePanel>
          <CollapsiblePanel
            ref={adGroupPanelRef}
            title="Ad Group"
            required
            collapsible={false}
            hasError={invalidPanels.has("adGroup")}
          >
            <AdGroupSelectionPanel panelMode />
          </CollapsiblePanel>
          {showOptional(CAMPAIGN_STEPS.CRM_RETARGETING_SEGMENTS) && (
            <CollapsiblePanel
              ref={crmRetargetingPanelRef}
              title="CRM Retargeting"
              required={hasPrescribedPanels}
              collapsible={!hasPrescribedPanels}
              includeToggle={showStepToggle}
              included={isStepIncluded(CAMPAIGN_STEPS.CRM_RETARGETING_SEGMENTS)}
              onIncludedChange={(v) => toggleStepInclusion(CAMPAIGN_STEPS.CRM_RETARGETING_SEGMENTS, v)}
              hasError={invalidPanels.has("crmRetargeting")}
            >
              <CRMRetargetingSegmentsView panelMode />
            </CollapsiblePanel>
          )}
          {showOptional(CAMPAIGN_STEPS.VISITORS_RETARGETING_SEGMENTS) && (
            <CollapsiblePanel
              ref={visitorsRetargetingPanelRef}
              title="Visitors Re-targeting Segments"
              required={hasPrescribedPanels}
              collapsible={!hasPrescribedPanels}
              includeToggle={showStepToggle}
              included={isStepIncluded(CAMPAIGN_STEPS.VISITORS_RETARGETING_SEGMENTS)}
              onIncludedChange={(v) => toggleStepInclusion(CAMPAIGN_STEPS.VISITORS_RETARGETING_SEGMENTS, v)}
              hasError={invalidPanels.has("visitorsRetargeting")}
            >
              <VisitorsRetargetingSegmentsView panelMode />
            </CollapsiblePanel>
          )}
          {showOptional(CAMPAIGN_STEPS.ZIP_CODES) && (
            <CollapsiblePanel
              ref={zipCodesPanelRef}
              title="Geography / Zip Codes"
              required={hasPrescribedPanels}
              collapsible={!hasPrescribedPanels}
              includeToggle={showStepToggle}
              included={isStepIncluded(CAMPAIGN_STEPS.ZIP_CODES)}
              onIncludedChange={(v) => toggleStepInclusion(CAMPAIGN_STEPS.ZIP_CODES, v)}
              hasError={invalidPanels.has("zipCodes")}
            >
              <ZipCodeSetSelectorPanel panelMode />
            </CollapsiblePanel>
          )}
          {showOptional(CAMPAIGN_STEPS.STATE_PROVINCES) && (
            <CollapsiblePanel
              ref={stateProvincePanelRef}
              title="Geography (State/Province)"
              required={hasPrescribedPanels}
              collapsible={!hasPrescribedPanels}
              includeToggle={showStepToggle}
              included={isStepIncluded(CAMPAIGN_STEPS.STATE_PROVINCES)}
              onIncludedChange={(v) => toggleStepInclusion(CAMPAIGN_STEPS.STATE_PROVINCES, v)}
              hasError={invalidPanels.has("stateProvinces")}
            >
              <StateProvinceSetSelectorPanel panelMode />
            </CollapsiblePanel>
          )}
          {showOptional(CAMPAIGN_STEPS.TARGET_COMPANIES) && (
            <CollapsiblePanel
              ref={targetCompaniesPanelRef}
              title="Target Companies"
              required={hasPrescribedPanels}
              collapsible={!hasPrescribedPanels}
              includeToggle={showStepToggle}
              included={isStepIncluded(CAMPAIGN_STEPS.TARGET_COMPANIES)}
              onIncludedChange={(v) => toggleStepInclusion(CAMPAIGN_STEPS.TARGET_COMPANIES, v)}
              hasError={invalidPanels.has("targetCompanies")}
            >
              <TargetCompaniesView />
            </CollapsiblePanel>
          )}
          {showOptional(CAMPAIGN_STEPS.ROLE_TITLES) && (
            <CollapsiblePanel
              ref={roleTitlesPanelRef}
              title="Roles & Titles"
              required={hasPrescribedPanels}
              collapsible={!hasPrescribedPanels}
              includeToggle={showStepToggle}
              included={isStepIncluded(CAMPAIGN_STEPS.ROLE_TITLES)}
              onIncludedChange={(v) => toggleStepInclusion(CAMPAIGN_STEPS.ROLE_TITLES, v)}
              hasError={invalidPanels.has("roleTitles")}
            >
              <RoleTitleSetSelectorPanel panelMode />
            </CollapsiblePanel>
          )}
          {showOptional(CAMPAIGN_STEPS.PUBLICATION_LISTS) && (
            <CollapsiblePanel
              ref={publicationListsPanelRef}
              title="Publication Lists"
              required={hasPrescribedPanels}
              collapsible={!hasPrescribedPanels}
              includeToggle={showStepToggle}
              included={isStepIncluded(CAMPAIGN_STEPS.PUBLICATION_LISTS)}
              onIncludedChange={(v) => toggleStepInclusion(CAMPAIGN_STEPS.PUBLICATION_LISTS, v)}
              hasError={invalidPanels.has("publicationLists")}
            >
              <PublicationListSetSelectorPanel panelMode />
            </CollapsiblePanel>
          )}
          <CollapsiblePanel title="Summary" collapsible={false}>
            <CampaignTemplateSummaryPanel mode={mode} />
          </CollapsiblePanel>
        </PanelStack>
      </PageContent>

      <StickyFooter>
        {onBack && (
          <BackButton onClick={onBack} disableElevation>
            Back
          </BackButton>
        )}
        {createError && <FooterErrorText>{createError}</FooterErrorText>}
        <CancelButton onClick={handleCancel} disableElevation>
          Cancel
        </CancelButton>
        <CreateButton onClick={handleCreate} disabled={isPending} disableElevation>
          {isPending ? (
            <CircularProgress size={16} style={{ color: "inherit" }} />
          ) : isTemplateMode && isEditMode ? (
            "Save Changes"
          ) : isTemplateMode ? (
            "Create Template"
          ) : (
            "Create Campaign"
          )}
        </CreateButton>
      </StickyFooter>

      <SuccessDialog
        isOpen={isSuccessOpen}
        handleClose={handleSuccessClose}
        title={isTemplateMode ? "Campaign Template Created Successfully!" : "Campaign Created Successfully!"}
      />

      <ConfirmDialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}>
        <ConfirmTitle>Are you sure?</ConfirmTitle>
        <ConfirmQuestion>
          {isTemplateMode && isEditMode
            ? "Save changes to this template?"
            : isTemplateMode
            ? "Create Campaign Template?"
            : "Create Campaign?"}
        </ConfirmQuestion>
        <ConfirmActions>
          <ConfirmCancelButton onClick={() => setIsConfirmOpen(false)} disableElevation>
            Cancel
          </ConfirmCancelButton>
          <ConfirmYesButton onClick={handleConfirmedCreate} disableElevation>
            Yes
          </ConfirmYesButton>
        </ConfirmActions>
      </ConfirmDialog>

      {campaignToCreate.selectedAdvertiser && !campaignToCreate.selectedAdvertiser.paymentMethod && (
        <NoPaymentMethodModal open advertiserId={campaignToCreate.selectedAdvertiser.brandgenAdvertiserId} />
      )}
    </>
  );
};

export default CampaignBuilderForm;
